import { useEffect, useRef, useState } from "react";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import HyperionToolTip from "../HyperionToolTip";
import AISparkleIcon from "../../assets/AISparkleIcon";
import {
  buildReviewerDisplayName,
  buildReviewerSecondaryLabel,
} from "../../utils/AiReviewHelpers";

function formatMatchLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/[a-z]/.test(raw) && !/_/.test(raw)) return raw;
  return raw
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeMatchLabels(values) {
  return (Array.isArray(values) ? values : [])
    .map((value) => formatMatchLabel(value))
    .filter(Boolean);
}

function TooltipPanel({ title, body, accent, T }) {
  return (
    <div
      style={{
        minWidth: 220,
        maxWidth: 290,
        borderRadius: 12,
        border: `1px solid ${accent || T.brandLight}44`,
        background: `linear-gradient(180deg, rgba(8,14,30,0.98) 0%, rgba(6,10,24,0.98) 100%)`,
        boxShadow: `0 14px 28px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.04)`,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "8px 10px 6px",
          borderBottom: `1px solid ${accent || T.brandLight}22`,
          background: `linear-gradient(180deg, ${accent || T.brandLight}14 0%, rgba(255,255,255,0.01) 100%)`,
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: "0.14em",
          color: accent || T.brandLight,
          textTransform: "uppercase",
          fontFamily: T.fontFamily,
        }}
      >
        {title}
      </div>
      <div
        style={{
          padding: "9px 10px 10px",
          fontSize: 11,
          lineHeight: 1.5,
          color: "#D6E4F7",
          fontFamily: T.fontFamily,
        }}
      >
        {body}
      </div>
    </div>
  );
}

function TooltipFitPanel({ score, state, T }) {
  const pct = Math.max(0, Math.min(100, Math.round(Number(score) || 0)));

  return (
    <div
      style={{
        width: 186,
        borderRadius: 10,
        border: `1px solid ${T.brandLight}44`,
        background: "linear-gradient(180deg, rgba(10,16,29,0.98) 0%, rgba(7,11,22,0.98) 100%)",
        boxShadow: "0 10px 22px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.03)",
        overflow: "hidden",
        padding: "10px 10px 9px",
      }}
    >
      <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: T.brandLight, fontFamily: T.fontFamily }}>
        Submitter topic fit
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 9 }}>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: `conic-gradient(${T.brandLight} ${pct * 3.6}deg, rgba(255,255,255,0.08) ${pct * 3.6}deg 360deg)`,
            boxShadow: `0 0 14px ${T.brandLight}16`,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "radial-gradient(circle at 35% 35%, rgba(22,39,67,0.98) 0%, rgba(8,13,24,1) 72%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#F2FAFF",
              fontSize: 12,
              fontWeight: 800,
              fontFamily: T.fontFamily,
            }}
          >
            {pct}%
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 7, minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#F3FBFF", lineHeight: 1, fontFamily: T.fontFamily }}>
            {state}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: T.submission, boxShadow: `0 0 8px ${T.submission}33` }} />
            <span style={{ width: 22, height: 1, background: "rgba(255,255,255,0.16)" }} />
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: T.brandLight, boxShadow: `0 0 8px ${T.brandLight}33` }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#C8D9EE", fontFamily: T.fontFamily }}>
              Match field
            </div>
            <div style={{ fontSize: 10, fontWeight: 800, color: "#F3FBFF", fontFamily: T.fontFamily }}>
              Topic
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TooltipMatchPanel({ submitterLabels, reviewerLabels, matchedLabels, overlapValue, T }) {
  const leftItems = submitterLabels.length ? submitterLabels : ["No submitter value"];
  const rightItems = reviewerLabels.length ? reviewerLabels : ["No specialist areas"];
  const matched = new Set((matchedLabels.length ? matchedLabels : ["Matched topic"]).map((label) => label.toLowerCase()));

  const renderLane = (items, tone, side) => (
    <div
      style={{
        display: "flex",
        gap: 6,
        alignItems: "center",
        minWidth: "max-content",
        paddingRight: 6,
      }}
    >
      {items.map((label, index) => {
        const isMatched = matched.has(label.toLowerCase());
        return (
          <div
            key={`${side}-${label}-${index}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "5px 10px",
              borderRadius: 999,
              border: `1px solid ${isMatched ? `${tone}55` : T.borderMedium}`,
              background: isMatched
                ? side === "left"
                  ? "linear-gradient(180deg, rgba(18,46,78,0.98) 0%, rgba(12,28,48,0.98) 100%)"
                  : "linear-gradient(180deg, rgba(22,44,78,0.98) 0%, rgba(11,24,46,0.98) 100%)"
                : "rgba(255,255,255,0.03)",
              color: "#F4FBFF",
              fontSize: 9,
              fontWeight: isMatched ? 800 : 700,
              letterSpacing: "0.03em",
              fontFamily: T.fontFamily,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {label}
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      style={{
        width: 440,
        borderRadius: 10,
        border: `1px solid ${T.submission}44`,
        background: "linear-gradient(180deg, rgba(10,16,29,0.98) 0%, rgba(7,11,22,0.98) 100%)",
        boxShadow: "0 10px 22px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.03)",
        overflow: "hidden",
        padding: "10px 10px 9px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: T.submission, fontFamily: T.fontFamily }}>
          Matched on
        </div>
        <div style={{ fontSize: 9, fontWeight: 800, color: "#EAF7FF", fontFamily: T.fontFamily }}>
          {overlapValue}
        </div>
      </div>
      <div
        style={{
          position: "relative",
          marginTop: 9,
          padding: "10px",
          borderRadius: 8,
          background: "linear-gradient(180deg, rgba(7,14,31,0.86) 0%, rgba(8,18,39,0.94) 100%)",
          border: `1px solid ${T.borderMedium}`,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 20,
            right: 20,
            top: 24,
            height: 1,
            background: `linear-gradient(90deg, ${T.submission} 0%, ${T.brandLight} 100%)`,
            opacity: 0.35,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 20,
            left: 20,
            width: 36,
            height: 10,
            borderRadius: 999,
            background: `linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 48%, transparent 100%)`,
            filter: "blur(1px)",
            opacity: 0.18,
          }}
        />
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 24px minmax(0, 1fr)", alignItems: "start", gap: 10 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: T.submission, fontFamily: T.fontFamily, whiteSpace: "nowrap" }}>
              Submitter chose
            </div>
            <div
              style={{
                overflowX: "auto",
                overflowY: "hidden",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {renderLane(leftItems, T.submission, "left")}
            </div>
          </div>
          <div style={{ position: "relative", alignSelf: "stretch", minHeight: 60 }}>
            <div style={{ position: "absolute", left: "50%", top: 14, bottom: 14, width: 1, transform: "translateX(-50%)", background: "rgba(255,255,255,0.10)" }} />
            <div style={{ position: "absolute", left: "50%", top: "50%", width: 18, height: 18, transform: "translate(-50%, -50%)", borderRadius: "50%", border: `1px solid ${T.brandLight}44`, background: "linear-gradient(180deg, rgba(22,38,62,0.98) 0%, rgba(10,18,34,0.98) 100%)", boxShadow: `0 0 10px ${T.brandLight}12` }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: T.brandLight, fontFamily: T.fontFamily, whiteSpace: "nowrap" }}>
              Reviewer specialist areas
            </div>
            <div
              style={{
                overflowX: "auto",
                overflowY: "hidden",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {renderLane(rightItems, T.brandLight, "right")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TooltipLoadPanel({ state, loadCount, completedCount, averageScore, tone, T }) {
  const activeBars = Math.max(1, Math.min(5, Number(loadCount) + 1));

  return (
    <div
      style={{
        width: 186,
        borderRadius: 10,
        border: `1px solid ${tone}44`,
        background: "linear-gradient(180deg, rgba(10,16,29,0.98) 0%, rgba(7,11,22,0.98) 100%)",
        boxShadow: "0 10px 22px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.03)",
        overflow: "hidden",
        padding: "10px 10px 9px",
      }}
    >
      <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: tone, fontFamily: T.fontFamily }}>
        Reviewer load
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 46, marginTop: 9 }}>
        {Array.from({ length: 5 }).map((_, index) => {
          const active = index < activeBars;
          const height = 14 + index * 6;
          return (
            <div
              key={index}
              style={{
                flex: 1,
                height,
                borderRadius: 999,
                background: active ? `linear-gradient(180deg, ${tone} 0%, ${tone}80 100%)` : "rgba(255,255,255,0.08)",
                boxShadow: active ? `0 0 10px ${tone}24` : "none",
                animation: active ? `airp-load-breathe 2.1s ease-in-out ${index * 120}ms infinite` : "none",
              }}
            />
          );
        })}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 9 }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: "#F4FBFF", lineHeight: 1, fontFamily: T.fontFamily }}>
          {state}
        </div>
        <div
          style={{
            padding: "3px 7px",
            borderRadius: 999,
            border: `1px solid ${T.borderMedium}`,
            background: "rgba(255,255,255,0.04)",
            color: "#D8E8F8",
            fontSize: 8,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontFamily: T.fontFamily,
          }}
        >
          {loadCount} active
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8, marginTop: 10 }}>
        <div style={{ padding: "6px 7px", borderRadius: 8, border: `1px solid ${T.borderMedium}`, background: "rgba(255,255,255,0.03)" }}>
          <div style={{ fontSize: 7, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "#9FB5CE", fontFamily: T.fontFamily }}>
            Completed
          </div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#F4FBFF", lineHeight: 1.1, marginTop: 4, fontFamily: T.fontFamily }}>
            {completedCount}
          </div>
        </div>
        <div style={{ padding: "6px 7px", borderRadius: 8, border: `1px solid ${T.borderMedium}`, background: "rgba(255,255,255,0.03)" }}>
          <div style={{ fontSize: 7, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "#9FB5CE", fontFamily: T.fontFamily }}>
            Avg score
          </div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#F4FBFF", lineHeight: 1.1, marginTop: 4, fontFamily: T.fontFamily }}>
            {Number.isFinite(Number(averageScore)) ? Number(averageScore).toFixed(1) : "-"}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCapsule({ Icon, value, T, tone, compact = false, tooltip = null }) {
  const color = tone || T.brandLight;
  const [open, setOpen] = useState(false);
  const capsuleWidth = compact ? 70 : 122;
  const capsuleMinHeight = compact ? 28 : 34;
  const iconSize = compact ? 16 : 20;
  const iconGlyphSize = compact ? 10 : 12;
  const valueFontSize = compact ? 8 : 10;
  const glow = `${color}2e`;

  const toggleOpen = (event) => {
    if (!tooltip) return;
    event?.stopPropagation?.();
    setOpen((current) => !current);
  };

  const close = () => setOpen(false);

  const capsule = (
    <div
      role={tooltip ? "button" : undefined}
      tabIndex={tooltip ? 0 : undefined}
      onClick={toggleOpen}
      onKeyDown={
        tooltip
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                toggleOpen(event);
              }
              if (event.key === "Escape") {
                close();
              }
            }
          : undefined
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: compact ? 6 : 8,
        width: capsuleWidth,
        minWidth: capsuleWidth,
        maxWidth: capsuleWidth,
        minHeight: capsuleMinHeight,
        padding: compact ? "4px 8px" : "6px 10px",
        borderRadius: 999,
        background: `linear-gradient(180deg, ${color}2a 0%, ${color}14 100%)`,
        border: `1.5px solid ${color}52`,
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(255,255,255,0.02), 0 8px 18px ${glow}`,
        cursor: tooltip ? "help" : "default",
        outline: "none",
        position: "relative",
        overflow: "hidden",
        backdropFilter: "blur(6px)",
      }}
    >
      <span
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 999,
          background: `linear-gradient(90deg, ${color}18 0%, transparent 48%, ${color}12 100%)`,
          opacity: 0.9,
          pointerEvents: "none",
        }}
      />
      <span
        style={{
          width: iconSize,
          height: iconSize,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(180deg, ${color}30 0%, ${color}18 100%)`,
          color: "#F7FBFF",
          flexShrink: 0,
          boxShadow: `inset 0 1px 0 rgba(255,255,255,0.12), 0 0 12px ${color}2a`,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Icon sx={{ fontSize: iconGlyphSize }} />
      </span>
      <span
        style={{
          fontSize: valueFontSize,
          fontWeight: 900,
          letterSpacing: compact ? "0.07em" : "0.09em",
          color: "#F4FBFF",
          textTransform: "uppercase",
          fontFamily: T.fontFamily,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
          flex: 1,
          position: "relative",
          zIndex: 1,
        }}
      >
        {value}
      </span>
    </div>
  );

  if (!tooltip) return capsule;

  return (
    <ClickAwayListener onClickAway={close}>
      <div style={{ display: "inline-flex" }}>
        <HyperionToolTip
          variant="dark"
          rich
          placement="top"
          title={tooltip}
          open={open}
          onClose={close}
          disableHoverListener
          disableFocusListener
          disableTouchListener
        >
          {capsule}
        </HyperionToolTip>
      </div>
    </ClickAwayListener>
  );
}

function getPrimaryMetricValues(reviewer, T) {
  const suitability = Number(reviewer?.matchScore ?? 0);
  const overlap = Number(reviewer?.stats?.overlap ?? 0);
  const load = Number(reviewer?.stats?.load ?? 0);

  return {
    matchValue: suitability >= 90 ? "PERFECT" : suitability >= 75 ? "STRONG" : "PARTIAL",
    overlapValue: overlap > 0 ? `${overlap}/${overlap}` : "0/0",
    workloadValue: load <= 1 ? "LOW" : load <= 4 ? "MED" : "HIGH",
    workloadTone: load <= 1 ? T.accepted : load <= 4 ? T.warning : T.rejected,
  };
}

function buildPrimaryCardRecommendation(rec) {
  if (!rec) return null;

  return {
    source: rec,
    reviewerId: rec.reviewerId,
    name: buildReviewerDisplayName(rec.reviewer),
    specialization: buildReviewerSecondaryLabel(rec.reviewer) || "",
    email: rec.reviewer?.email || rec.reviewer?.userEmail || "",
    availability: "available",
    matchScore: rec.suitabilityPercentage ?? 0,
    reason: rec.confidenceReason || null,
    stats: {
      suitability: rec.suitabilityPercentage ?? 0,
      overlap: rec.overlapCount ?? 0,
      submissionAoE: Array.isArray(rec.submissionAoELabels) ? rec.submissionAoELabels : Array.isArray(rec.submissionAoE) ? rec.submissionAoE : [],
      reviewerAoE: Array.isArray(rec.reviewerAoELabels) ? rec.reviewerAoELabels : Array.isArray(rec.reviewerAoE) ? rec.reviewerAoE : [],
      overlapValues: Array.isArray(rec.overlapValueLabels) ? rec.overlapValueLabels : Array.isArray(rec.overlapValues) ? rec.overlapValues : [],
      submissionCount: rec.submissionAoE?.length ?? rec.overlapCount ?? 0,
      reviewerCount: rec.reviewerAoECount ?? rec.overlapCount ?? 0,
      load: rec.currentReviewCount ?? 0,
      completedCount: rec.reviewerMetrics?.completedCount ?? 0,
      averageScore: rec.reviewerMetrics?.averageScore ?? null,
      lead: rec.gapToNext ?? 0,
    },
  };
}

function buildAlternativeCardRecommendation(rec) {
  if (!rec) return null;

  return {
    source: rec,
    reviewerId: rec.reviewerId,
    name: buildReviewerDisplayName(rec.reviewer),
    specialization: buildReviewerSecondaryLabel(rec.reviewer) || "",
    email: rec.reviewer?.email || rec.reviewer?.userEmail || "",
    matchScore: rec.suitabilityPercentage ?? 0,
    stats: {
      overlap: rec.overlapCount ?? 0,
      load: rec.currentReviewCount ?? 0,
    },
  };
}

function AiRecommendationMechanism({ T }) {
  const content = (
    <div
      style={{
        width: 122,
        minWidth: 122,
        height: 112,
        minHeight: 112,
        boxSizing: "border-box",
        borderRadius: 14,
        border: `1px solid ${T.review}44`,
        background: `linear-gradient(180deg, ${T.surface2} 0%, rgba(11,18,42,0.98) 100%)`,
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px ${T.review}12`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: "10px 10px 8px",
        flexShrink: 0,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <span
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(circle at 50% 36%, ${T.review}14 0%, transparent 62%)`,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          width: 58,
          height: 58,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: T.review,
          background: `radial-gradient(circle at 35% 35%, ${T.review}14 0%, ${T.surface2} 72%)`,
          border: `1px solid ${T.review}26`,
          boxShadow: `0 0 0 8px ${T.review}10, 0 0 0 16px ${T.brandLight}08`,
          position: "relative",
          zIndex: 1,
        }}
      >
        <AISparkleIcon sx={{ fontSize: 32 }} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, position: "relative", zIndex: 1 }}>
        <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: "0.12em", color: T.textSecondary, textTransform: "uppercase", fontFamily: T.fontFamily }}>
          Best fit reviewer
        </span>
      </div>
    </div>
  );

  return (
    <HyperionToolTip
      variant="dark"
      rich
      placement="top"
      title={
        <TooltipPanel
          title="Best fit reviewer"
          body="This is the current top-ranked reviewer from the assignment engine, based on the configured submission-to-reviewer topic match plus workload and availability checks."
          accent={T.review}
          T={T}
        />
      }
    >
      {content}
    </HyperionToolTip>
  );
}

function AssignHoldControl({ reviewer, T, onAssign, assignmentState }) {
  const holdMs = 700;
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const completedRef = useRef(false);

  const [progress, setProgress] = useState(0);
  const [holding, setHolding] = useState(false);

  useEffect(() => {
    setProgress(0);
    setHolding(false);
    completedRef.current = false;
    cancelAnimationFrame(rafRef.current);
  }, [reviewer]);

  const reviewerId = reviewer?.reviewerId ?? null;
  const isAssigning = assignmentState?.status === "assigning" && assignmentState?.reviewerId === reviewerId;
  const isAssigned = assignmentState?.status === "assigned" && assignmentState?.reviewerId === reviewerId;

  useEffect(() => {
    if (!isAssigning && !isAssigned) {
      setProgress(0);
      setHolding(false);
      completedRef.current = false;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      startRef.current = null;
    }
  }, [isAssigning, isAssigned]);

  const stop = () => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    startRef.current = null;
    setHolding(false);
    if (!completedRef.current) setProgress(0);
  };

  const step = (ts) => {
    if (!startRef.current) startRef.current = ts;
    const elapsed = ts - startRef.current;
    const next = Math.min(elapsed / holdMs, 1);
    setProgress(next);

    if (next >= 1 && !completedRef.current) {
      completedRef.current = true;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      setHolding(false);
      setProgress(1);
      onAssign?.(reviewer);
      return;
    }

    rafRef.current = requestAnimationFrame(step);
  };

  const handleStart = (e) => {
    e.stopPropagation();
    if (isAssigned || isAssigning || !reviewer) return;
    completedRef.current = false;
    setHolding(true);
    setProgress(0);
    startRef.current = null;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(step);
  };

  const handleEnd = (e) => {
    e.stopPropagation();
    stop();
  };

  const holdDegrees = Math.max(0, Math.min(360, progress * 360));
  const controlColor = isAssigned ? T.accepted : isAssigning ? T.brandLight : T.review;
  const ringBackground = `conic-gradient(${controlColor} ${holdDegrees}deg, rgba(255,255,255,0.06) ${holdDegrees}deg 360deg)`;

  return (
    <button type="button" onMouseDown={handleStart} onMouseUp={handleEnd} onMouseLeave={handleEnd} onTouchStart={handleStart} onTouchEnd={handleEnd} onTouchCancel={handleEnd} aria-label={isAssigned ? "Reviewer assigned" : isAssigning ? "Assigning reviewer" : "Press and hold to assign reviewer"} style={{ width: 108, minWidth: 108, height: 108, borderRadius: "50%", border: "none", background: "transparent", cursor: isAssigned || isAssigning ? "default" : "pointer", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", flexShrink: 0 }}>
      <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: ringBackground, padding: 7, boxShadow: isAssigned ? `0 0 34px ${T.accepted}65` : isAssigning ? `0 0 36px ${T.brandLight}60` : holding ? `0 0 44px ${T.review}72` : `0 0 30px ${T.review}34`, transition: holding || isAssigned || isAssigning ? "none" : "box-shadow 0.2s ease" }}>
        <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: `radial-gradient(circle at 35% 30%, ${T.surface2} 0%, ${T.panelBg} 75%)`, border: `3px solid ${isAssigned ? `${T.accepted}70` : isAssigning ? `${T.brandLight}70` : `${T.review}40`}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", transform: holding ? "scale(0.97)" : "scale(1)", transition: holding || isAssigning ? "none" : "transform 0.18s ease" }}>
          {isAssigned ? (
            <>
              <div style={{ width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 6 }}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M4.5 10.5l3.2 3.2L15.5 6.5" stroke={T.accepted} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", color: T.accepted, textTransform: "uppercase", fontFamily: T.fontFamily }}>
                Assigned
              </span>
            </>
          ) : isAssigning ? (
            <>
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", color: T.brandLight, textTransform: "uppercase", fontFamily: T.fontFamily, marginBottom: 2 }}>
                Assigning
              </span>
              <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.08em", color: T.textSecondary, textTransform: "uppercase", fontFamily: T.fontFamily }}>
                Working
              </span>
            </>
          ) : (
            <>
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.12em", color: holding ? T.textPrimary : T.review, textTransform: "uppercase", fontFamily: T.fontFamily, marginBottom: 2 }}>
                Assign
              </span>
              <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.08em", color: T.textSecondary, textTransform: "uppercase", fontFamily: T.fontFamily }}>
                Hold
              </span>
            </>
          )}
        </div>
      </div>
    </button>
  );
}

function ReviewerCardPrimary({ reviewer, T, onAssign, assignmentState }) {
  if (!reviewer) {
    return (
      <div style={{ display: "flex", gap: 12, alignItems: "stretch", animation: "airp-reviewerSwap 0.26s ease-out both" }}>
      <div style={{ flex: 1, minWidth: 0, padding: "12px 14px", background: T.surface1, borderRadius: 14, border: `1.5px dashed ${T.borderMedium}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.textMuted, fontFamily: T.fontFamily }}>No reviewer recommendation</div>
            <div style={{ fontSize: 10, color: T.textMuted, marginTop: 3, opacity: 0.7, fontFamily: T.fontFamily }}>Awaiting AI assignment engine</div>
          </div>
          <AiRecommendationMechanism T={T} />
        </div>
      </div>
    );
  }

  const initials = (reviewer.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  const { matchValue, overlapValue, workloadValue, workloadTone } = getPrimaryMetricValues(reviewer, T);
  const submitterLabels = normalizeMatchLabels(reviewer?.stats?.submissionAoE);
  const reviewerLabels = normalizeMatchLabels(reviewer?.stats?.reviewerAoE);
  const overlapLabels = normalizeMatchLabels(reviewer?.stats?.overlapValues);
  const matchScore = Number(reviewer?.stats?.suitability ?? reviewer?.matchScore ?? 0);
  const loadCount = Number(reviewer?.stats?.load ?? 0);
  const completedCount = Number(reviewer?.stats?.completedCount ?? 0);
  const averageScore = reviewer?.stats?.averageScore ?? null;

  return (
    <div style={{ display: "flex", gap: 12, alignItems: "stretch", animation: "airp-reviewerSwap 0.26s ease-out both" }}>
      <div style={{ flex: 1, minWidth: 0, borderRadius: 14, background: "transparent", display: "flex", alignItems: "stretch", overflow: "visible", gap: 10 }}>
        <div style={{ padding: 0, display: "flex", alignItems: "stretch" }}>
          <AiRecommendationMechanism T={T} />
        </div>
        <div style={{ flex: 1, minWidth: 0, padding: 0, display: "flex", alignItems: "stretch" }}>
          <div style={{ flex: 1, minWidth: 0, height: 112, minHeight: 112, boxSizing: "border-box", padding: "10px 12px 10px 14px", borderRadius: 14, border: `1px solid ${T.borderMedium}`, background: `linear-gradient(180deg, ${T.surface1} 0%, rgba(9,20,43,0.96) 100%)`, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                <div style={{ width: 42, height: 42, borderRadius: "50%", flexShrink: 0, backgroundColor: T.brandLight, boxShadow: `0 0 0 1px ${T.brand}24, inset 0 1px 0 rgba(255,255,255,0.16)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: "white", letterSpacing: "-0.03em", fontFamily: T.fontFamily }}>
                  {initials}
                </div>
                <div style={{ flex: 1, minWidth: 0, paddingTop: 10 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: T.fontFamily }}>
                    {reviewer.name}
                  </div>
                  <div style={{ fontSize: 10, color: T.textSecondary, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: T.fontFamily }}>
                    {reviewer.email || reviewer.specialization}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                <MetricCapsule
                  Icon={WorkspacePremiumRoundedIcon}
                  value={matchValue}
                  T={T}
                  tone={T.brandLight}
                  tooltip={<TooltipFitPanel score={matchScore} state={matchValue} T={T} />}
                />
                <MetricCapsule
                  Icon={HubRoundedIcon}
                  value={overlapValue}
                  T={T}
                  tone={T.submission}
                  tooltip={<TooltipMatchPanel submitterLabels={submitterLabels} reviewerLabels={reviewerLabels} matchedLabels={overlapLabels} overlapValue={overlapValue} T={T} />}
                />
                <MetricCapsule
                  Icon={ScheduleRoundedIcon}
                  value={workloadValue}
                  T={T}
                  tone={workloadTone}
                  tooltip={<TooltipLoadPanel state={workloadValue} loadCount={loadCount} completedCount={completedCount} averageScore={averageScore} tone={workloadTone} T={T} />}
                />
              </div>

              {reviewer.reason && (
                <div style={{ fontSize: 10, color: T.textMuted, lineHeight: 1.45, fontFamily: T.fontFamily, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {reviewer.reason}
                </div>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <AssignHoldControl reviewer={reviewer} T={T} onAssign={onAssign} assignmentState={assignmentState} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewerRowAlt({ reviewer, T, index, onSelect }) {
  const hue = (index * 70 + 195) % 360;
  const delay = 320 + index * 56;
  const initials = (reviewer.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  const overlap = Number(reviewer?.stats?.overlap ?? 0);
  const load = Number(reviewer?.stats?.load ?? 0);
  const overlapValue = overlap > 0 ? `${overlap}/${overlap}` : "0/0";
  const workloadValue = load <= 1 ? "LOW" : load <= 4 ? "MED" : "HIGH";
  const workloadTone = load <= 1 ? T.accepted : load <= 4 ? T.warning : T.rejected;

  return (
    <button type="button" onClick={onSelect} style={{ width: "100%", minWidth: 0, display: "flex", flexDirection: "column", alignItems: "stretch", gap: 8, padding: "10px 10px 9px", background: `linear-gradient(180deg, rgba(11,25,52,0.98) 0%, rgba(8,17,39,0.98) 100%)`, borderRadius: 10, border: `1px solid ${T.gold}5E`, cursor: "pointer", animation: `airp-slideUp 0.22s ${delay}ms ease-out both`, textAlign: "left", boxShadow: `inset 0 1px 0 rgba(255,255,255,0.03), 0 0 0 1px ${T.gold}22` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", minWidth: 0 }}>
        <div style={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0, background: `hsl(${hue},52%,35%)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "white", letterSpacing: "-0.02em", fontFamily: T.fontFamily }}>
        {initials}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textPrimary, fontFamily: T.fontFamily, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {reviewer.name}
          </div>
          <div style={{ fontSize: 8, color: T.textSecondary, fontFamily: T.fontFamily, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {reviewer.email || reviewer.specialization}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, width: "100%", flexWrap: "nowrap", justifyContent: "center", alignItems: "center" }}>
        <MetricCapsule Icon={HubRoundedIcon} value={overlapValue} T={T} tone={T.submission} compact />
        <MetricCapsule Icon={ScheduleRoundedIcon} value={workloadValue} T={T} tone={workloadTone} compact />
      </div>
    </button>
  );
}

function ReviewerProfilePanel({ reviewer, T, onAssign, assignmentState }) {
  const initials = (reviewer.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  const { matchValue, overlapValue, workloadValue, workloadTone } = getPrimaryMetricValues(reviewer, T);
  const submitterLabels = normalizeMatchLabels(reviewer?.stats?.submissionAoE);
  const reviewerLabels = normalizeMatchLabels(reviewer?.stats?.reviewerAoE);
  const overlapLabels = normalizeMatchLabels(reviewer?.stats?.overlapValues);
  const matchScore = Number(reviewer?.stats?.suitability ?? reviewer?.matchScore ?? 0);
  const loadCount = Number(reviewer?.stats?.load ?? 0);
  const completedCount = Number(reviewer?.stats?.completedCount ?? 0);
  const averageScore = reviewer?.stats?.averageScore ?? null;

  return (
    <div style={{ flex: 1, minWidth: 0, minHeight: 188, height: 188, boxSizing: "border-box", padding: "10px 14px 12px", borderRadius: 14, border: `1px solid ${T.borderMedium}`, background: `linear-gradient(180deg, ${T.surface1} 0%, rgba(9,20,43,0.96) 100%)`, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)", display: "grid", gridTemplateColumns: "minmax(0, 1fr) 112px", columnGap: 16, alignItems: "center", overflow: "hidden", animation: "airp-reviewerSwap 0.26s ease-out both" }}>
      <div style={{ minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "flex-start", gap: 10 }}>
        <HyperionToolTip
          variant="dark"
          rich
          placement="top"
          title={
            <TooltipPanel
              title="Best fit reviewer"
              body="This is the current top-ranked reviewer from the assignment engine, based on configured topic matching and reviewer load checks."
              accent={T.review}
              T={T}
            />
          }
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 9px",
              borderRadius: 999,
              border: `1px solid ${T.review}2F`,
              background: `linear-gradient(180deg, rgba(34,20,60,0.94) 0%, rgba(13,15,39,0.96) 100%)`,
              boxShadow: `0 0 16px ${T.review}12, inset 0 1px 0 rgba(255,255,255,0.04)`,
              alignSelf: "flex-start",
              cursor: "help",
            }}
          >
            <AISparkleIcon sx={{ fontSize: 18, color: T.review }} />
            <span style={{ fontSize: 7, fontWeight: 800, letterSpacing: "0.12em", color: T.textSecondary, textTransform: "uppercase", fontFamily: T.fontFamily }}>
              Best fit reviewer
            </span>
          </div>
        </HyperionToolTip>
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, marginTop: 2 }}>
          <div style={{ width: 42, height: 42, borderRadius: "50%", flexShrink: 0, backgroundColor: T.brandLight, boxShadow: `0 0 0 1px ${T.brand}24, inset 0 1px 0 rgba(255,255,255,0.16)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: "white", letterSpacing: "-0.03em", fontFamily: T.fontFamily }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: T.fontFamily }}>
              {reviewer.name}
            </div>
            <div style={{ fontSize: 10, color: T.textSecondary, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: T.fontFamily }}>
              {reviewer.email || reviewer.specialization}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", paddingTop: 2, justifyContent: "flex-start" }}>
          <MetricCapsule
            Icon={WorkspacePremiumRoundedIcon}
            value={matchValue}
            T={T}
            tone={T.brandLight}
            tooltip={<TooltipFitPanel score={matchScore} state={matchValue} T={T} />}
          />
          <MetricCapsule
            Icon={HubRoundedIcon}
            value={overlapValue}
            T={T}
            tone={T.submission}
            tooltip={<TooltipMatchPanel submitterLabels={submitterLabels} reviewerLabels={reviewerLabels} matchedLabels={overlapLabels} overlapValue={overlapValue} T={T} />}
          />
          <MetricCapsule
            Icon={ScheduleRoundedIcon}
            value={workloadValue}
            T={T}
            tone={workloadTone}
            tooltip={<TooltipLoadPanel state={workloadValue} loadCount={loadCount} completedCount={completedCount} averageScore={averageScore} tone={workloadTone} T={T} />}
          />
        </div>

        {reviewer.reason && (
          <div style={{ fontSize: 10, color: T.textMuted, lineHeight: 1.45, fontFamily: T.fontFamily, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {reviewer.reason}
          </div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <AssignHoldControl reviewer={reviewer} T={T} onAssign={onAssign} assignmentState={assignmentState} />
      </div>
    </div>
  );
}

function ExpandedAlternativeCard({ reviewer, T, index, onSelect }) {
  const initials = (reviewer.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  const load = Number(reviewer?.stats?.load ?? 0);
  const overlap = Number(reviewer?.stats?.overlap ?? 0);
  const overlapValue = overlap > 0 ? `${overlap}/${overlap}` : "0/0";
  const workloadValue = load <= 1 ? "LOW" : load <= 4 ? "MED" : "HIGH";
  const workloadTone = load <= 1 ? T.accepted : load <= 4 ? T.warning : T.rejected;
  const hue = (index * 70 + 195) % 360;

  return (
    <button
      type="button"
      onClick={() => onSelect?.(reviewer.source)}
      style={{
        minWidth: 0,
        borderRadius: 10,
        border: `1px solid ${T.gold}66`,
        background: `linear-gradient(180deg, rgba(11,25,52,0.98) 0%, rgba(8,17,39,0.98) 100%)`,
        padding: "9px 10px",
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.03), 0 0 0 1px ${T.gold}22`,
        minHeight: 42,
      }}
    >
      <div style={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0, background: `hsl(${hue},52%,35%)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "white", letterSpacing: "-0.02em", fontFamily: T.fontFamily }}>
        {initials}
      </div>
      <div style={{ minWidth: 0, flex: 1, display: "flex", alignItems: "baseline", gap: 6, whiteSpace: "nowrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.textPrimary, fontFamily: T.fontFamily, overflow: "hidden", textOverflow: "ellipsis", flexShrink: 0 }}>
          {reviewer.name}
        </span>
        <span style={{ fontSize: 8, color: T.textSecondary, fontFamily: T.fontFamily, overflow: "hidden", textOverflow: "ellipsis", minWidth: 0, flex: 1 }}>
          {reviewer.email || reviewer.specialization}
        </span>
      </div>
      <div style={{ display: "flex", gap: 4, flexWrap: "nowrap", justifyContent: "flex-end", alignItems: "center", flexShrink: 0 }}>
        <MetricCapsule Icon={HubRoundedIcon} value={overlapValue} T={T} tone={T.submission} compact />
        <MetricCapsule Icon={ScheduleRoundedIcon} value={workloadValue} T={T} tone={workloadTone} compact />
      </div>
    </button>
  );
}

function ExpandedAlternativesPanel({ altCards, T, onSelectRecommendation }) {
  const panelWidth = 518;

  const paddedCards = [...altCards];
  while (paddedCards.length < 3) paddedCards.push(null);

  return (
    <div style={{ width: panelWidth, flex: `0 0 ${panelWidth}px`, minHeight: 188, height: 188, padding: "10px 12px", borderRadius: 12, border: `1.5px solid ${T.borderMedium}`, background: `linear-gradient(180deg, ${T.surface1} 0%, rgba(8,15,36,0.94) 100%)`, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", color: T.textSecondary, textTransform: "uppercase", fontFamily: T.fontFamily }}>
          {`Alternative reviewers (${altCards.length})`}
        </div>
        <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.brandLight, opacity: 0.55, flexShrink: 0 }} />
        <div style={{ fontSize: 9, color: T.textSecondary, fontFamily: T.fontFamily, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          Also considered by AI
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
        {paddedCards.map((r, i) => (
          r ? (
            <ExpandedAlternativeCard key={`${r.name}-${i}`} reviewer={r} T={T} index={i} onSelect={onSelectRecommendation} />
          ) : (
            <div
              key={`placeholder-${i}`}
              style={{
                borderRadius: 10,
                border: `1px dashed ${T.borderSubtle}`,
                background: "rgba(255,255,255,0.01)",
                minHeight: 64,
              }}
            />
          )
        ))}
      </div>
    </div>
  );
}

function CollapsedAlternativesPanel({ altCards, T, onSelectRecommendation }) {
  const paddedCards = [...altCards];
  while (paddedCards.length < 3) paddedCards.push(null);

  return (
    <div style={{ padding: "7px 10px", borderRadius: 12, border: `1.5px solid ${T.borderMedium}`, background: `linear-gradient(180deg, ${T.surface1} 0%, rgba(8,15,36,0.94) 100%)`, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", color: T.textSecondary, textTransform: "uppercase", fontFamily: T.fontFamily }}>
          {`Alternative reviewers (${altCards.length})`}
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}>
          <HelpOutlineRoundedIcon sx={{ fontSize: 13, color: T.textMuted, flexShrink: 0 }} />
          <span style={{ fontSize: 9, color: T.textMuted, fontFamily: T.fontFamily, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            Other strong reviewer options
          </span>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "stretch", justifyContent: "flex-start" }}>
        {paddedCards.map((r, i) => (
          <div key={r ? `${r.name}-${i}` : `placeholder-${i}`} style={{ width: 168, flex: "0 0 168px", minWidth: 168 }}>
            {r ? (
              <ReviewerRowAlt reviewer={r} T={T} index={i} onSelect={() => onSelectRecommendation?.(r.source)} />
            ) : (
              <div
                style={{
                  borderRadius: 10,
                  border: `1px dashed ${T.borderSubtle}`,
                  background: "rgba(255,255,255,0.01)",
                  minHeight: 64,
                  height: "100%",
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function AiReviewPopupAssignmentSection({
  expanded,
  recommendationError,
  selectedRecommendation,
  alternativeRecommendations,
  onSelectRecommendation,
  specialist,
  onAssignRecommendation,
  assignmentState,
  T,
}) {
  /*
   * TODO: reviewer-fit additions for this column only
   *
   * - Add AI speciality recommendation as explicit reviewer-fit evidence.
   * - Add top 2-3 AI speciality matches as nearby acceptable expertise evidence.
   * - Add topic ambiguity as a routing caution to avoid the wrong reviewer domain.
   * - Add specialist-review reason sourced from admin operational guidance.
   *
   * Do not add presentation, author-feedback, vagueness, title-alignment,
   * or structural-balance detail here unless they become directly relevant
   * to reviewer assignment.
   */
  if (!T) return null;

  const primaryCard = selectedRecommendation ? buildPrimaryCardRecommendation(selectedRecommendation) : null;
  const altCards = (alternativeRecommendations || [])
    .slice(0, 3)
    .map((rec) => buildAlternativeCardRecommendation(rec))
    .filter(Boolean);
  return (
    <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 12 }}>
      {specialist && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 13px", borderRadius: 8, background: `${T.gold}14`, border: `1.5px solid ${T.gold}50` }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 18, color: T.gold, flexShrink: 0, mt: "1px" }} />
          <div style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.5, fontFamily: T.fontFamily }}>
            <span style={{ fontWeight: 700, color: T.gold }}>Specialist flag active.</span>{" "}
            Reviewer suggestions are guidance only. Specialist confirmation is required before routing.
          </div>
        </div>
      )}

      {recommendationError ? (
        <div style={{ padding: "11px 13px", background: T.surface1, borderRadius: 10, border: `1.5px dashed ${T.borderMedium}` }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.rejected, fontFamily: T.fontFamily }}>Recommendation engine could not evaluate</div>
          <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4, fontFamily: T.fontFamily }}>{String(recommendationError.message || recommendationError)}</div>
        </div>
      ) : !expanded ? (
        <ReviewerCardPrimary key={primaryCard?.reviewerId ?? "empty"} reviewer={primaryCard} T={T} onAssign={onAssignRecommendation} assignmentState={assignmentState} />
      ) : null}

      {expanded && primaryCard && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, animation: "airp-slideUp 0.24s 260ms ease-out both" }}>
          <ReviewerProfilePanel key={primaryCard?.reviewerId ?? "empty"} reviewer={primaryCard} T={T} onAssign={onAssignRecommendation} assignmentState={assignmentState} />
          <ExpandedAlternativesPanel altCards={altCards} T={T} onSelectRecommendation={onSelectRecommendation} />
        </div>
      )}

      {!expanded && altCards.length > 0 && (
        <CollapsedAlternativesPanel
          altCards={altCards}
          T={T}
          onSelectRecommendation={onSelectRecommendation}
        />
      )}

    </div>
  );
}

